import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useSearchParams } from "react-router-dom";

import {
  spacing,
  typography,
  colors,
} from "../../../../../../../../../assets/styles/tokens";
import WithoutFtd from "./WithoutFtd/WithoutFtd";
import NoPlayer from "./NoPlayer/NoPlayer";

const tabConfig = [
  { label: "Without FTD & NetRev", value: "WithoutFtd", trackerId: "643" },
  { label: "No Player Report", value: "NoPlayer", trackerId: "647" },
];

const TAB_COMPONENTS = [WithoutFtd, NoPlayer];

interface TabPanelProps {
  children?: React.ReactNode;
  activeIndex: number;
  index: number;
  visited: boolean;
}

// Only renders children if tab has been visited at least once
// Once visited, keeps mounted with display:none (no re-mount, no re-fetch)
const TabPanel = React.memo(
  ({ children, activeIndex, index, visited }: TabPanelProps) => {
    if (!visited) return null; // never visited → don't mount → no API call
    return (
      <Box sx={{ display: activeIndex === index ? "block" : "none", pt: 1 }}>
        {children}
      </Box>
    );
  },
);

export default function BrandsTabs() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabParam = searchParams.get("tabName");
  const initialIndex = tabConfig.findIndex(
    (tab) => tab.value === currentTabParam,
  );
  const activeIndex = initialIndex !== -1 ? initialIndex : 0;

  const [value, setValue] = React.useState(activeIndex);

  // Track which tabs have been visited — only visited tabs are mounted
  // On refresh: only the active tab index is pre-visited
  const workspaceId = searchParams.get("workspace");
  const [visitedTabs, setVisitedTabs] = React.useState<Set<number>>(
    () => new Set([activeIndex]),
  );

  // Reset visited tabs when workspace changes
  const prevWorkspaceRef = React.useRef(workspaceId);
  if (prevWorkspaceRef.current !== workspaceId) {
    prevWorkspaceRef.current = workspaceId;
    setVisitedTabs(new Set([value])); // ← runs during render, before effects
  }

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    // Mark this tab as visited so it mounts for the first time
    setVisitedTabs((prev) => new Set(prev).add(newValue));
    const newTabValue = tabConfig[newValue].value;
    searchParams.set("tabName", newTabValue);
    setSearchParams(searchParams);
  };

  return (
    <Box sx={{ height: "93vh", overflowY: "auto" }}>
      <Tabs
        value={value}
        onChange={handleChange}
        centered
        sx={{
          "& .MuiTabs-scrollButtons": {
            color: colors.GrayDarkMode[700].$value,
          },
          background: colors.GrayLightMode[100].$value,
          borderTop: "3px solid" + colors.GrayLightMode[100].$value,
          borderBottom: "3px solid" + colors.GrayLightMode[100].$value,
          borderRadius: spacing.Radius.md.$value,
          minHeight: spacing.Spacing["3xl"].$value,
        }}
        TabIndicatorProps={{
          sx: {
            height: spacing.Spacing.none.$value,
          },
        }}
      >
        {tabConfig.map((tab, index) => (
          <Tab
            key={tab.label}
            // disabled={loadingTrendsMobData}
            label={tab.label}
            disableRipple
            {...(tab.trackerId && value === index
              ? { "data-tracker": tab.trackerId }
              : {})}
            sx={{
              width: "49.1%",
              color: colors.GrayDarkMode[500].$value,
              textTransform: "none",
              fontWeight: typography.Font_weight.Regular.$value,
              borderRadius: spacing.Radius.xs.$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              padding: 0,
              minHeight: spacing.Spacing["3xl"].$value,
              "&.Mui-selected": {
                color: colors.GrayDarkMode[900].$value,
                fontWeight: typography.Font_weight.Bold.$value,
                backgroundColor: colors.Base.White.$value + "!important",
              },
              "&:hover": { backgroundColor: "transparent" },
            }}
          />
        ))}
      </Tabs>

      {TAB_COMPONENTS.map((Component, index) => (
        <TabPanel
          key={tabConfig[index].value}
          activeIndex={value}
          index={index}
          visited={visitedTabs.has(index)}
        >
          <Component />
        </TabPanel>
      ))}
    </Box>
  );
}
